import CRMStatusChip from '@components/Common/CRMStatusChip';
import {
  ActivityRail,
  EntityWorkspace,
  IdentityRail,
  SectionCard,
  WorkspaceField,
} from '@components/Common/EntityWorkspace';
import type { GlanceRow, TimelineEntry, WorkspaceTab } from '@components/Common/EntityWorkspace';
import { useActivityRail } from '@components/Common/EntityWorkspace/useActivityRail';
import ErrorMessage from '@components/Common/ErrorMessage';
import RecordLink from '@components/Common/RecordLink';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import { getLeadsQuery } from '@constants/graphQLQueries';
import { formatDate, formatDateTime } from '@helpers/date';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { humanizeEnum } from '@helpers/labels';
import { lastListView } from '@helpers/listView';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import { Button } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { findLead, leadInitials, leadName, leadRoles, openingsLabel, type Lead } from './lead';

/**
 * One Premium Hiring lead, as an entity workspace (owner, staging review: the old drawer "doesn't
 * follow our theme, reusable styles and reusable components").
 *
 * Read-only, as the drawer was. A lead is triage: when it has a linked employer, the contract is
 * sent from that employer's workspace, which is what the primary action opens.
 *
 * There is no single-lead query (lead.graphqls has `getLeads` only), so this loads the list and
 * picks the record by id. `getLeads` is one unpaginated array, so no lead can be missed by it.
 */

const TABS: WorkspaceTab[] = [{ id: 'overview', label: 'Overview', icon: <InfoOutlinedIcon /> }];

const LeadWorkspace: React.FC = () => {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activityOpen, toggleActivity] = useActivityRail();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await fetchGraphQLApiData(getLeadsQuery());
      const found = findLead(data?.getLeads, leadId);
      setLead(found);
      if (!found) setError('This lead could not be found. It may have been removed.');
    } catch {
      setError('Failed to load the lead. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !lead) return <SkeletonLoader variant="workspace" />;
  if (error || !lead) return <ErrorMessage page message={error ?? 'This lead could not be found.'} onRetry={load} />;

  const roles = leadRoles(lead);
  const openings = openingsLabel(lead.approxOpenings);

  const glance: GlanceRow[] = [
    { key: 'Contact', value: lead.contactPerson || '-' },
    { key: 'City', value: lead.city || '-' },
    { key: 'Source', value: humanizeEnum(lead.source) },
    { key: 'Openings', value: openings ?? '-' },
    { key: 'Received', value: formatDate(lead.createdAt), mono: true },
  ];

  const identityRail = (
    <IdentityRail
      initials={leadInitials(lead)}
      name={leadName(lead)}
      subId={lead._id}
      statusChip={lead.status ? <CRMStatusChip status={lead.status} /> : undefined}
      primaryAction={
        lead.employerId ? (
          <Button
            variant="contained"
            startIcon={<OpenInNewOutlinedIcon />}
            onClick={() => navigate(`/employers/${lead.employerId}`)}
          >
            Open linked employer
          </Button>
        ) : undefined
      }
      glance={glance}
      audit={{ createdAt: lead.createdAt ?? undefined }}
    />
  );

  const overview = (
    <>
      <SectionCard title="Contact" icon={<ContactPhoneOutlinedIcon />}>
        <WorkspaceField label="Contact person" value={lead.contactPerson} />
        <WorkspaceField
          label="Phone"
          mono
          value={lead.phone ? <a className="cc-link" href={`tel:${lead.phone}`}>{lead.phone}</a> : null}
        />
        <WorkspaceField
          label="Work email"
          value={lead.workEmail ? <a className="cc-link" href={`mailto:${lead.workEmail}`}>{lead.workEmail}</a> : null}
        />
        <WorkspaceField label="Company" value={lead.company} />
        <WorkspaceField label="City" value={lead.city} />
      </SectionCard>

      <SectionCard title="Hiring need" icon={<WorkOutlineOutlinedIcon />} meta={roles.length ? `${roles.length} ${roles.length === 1 ? 'role' : 'roles'}` : undefined}>
        <WorkspaceField label="Roles" full value={roles.length ? roles.join(', ') : 'No roles listed'} />
        <WorkspaceField label="Approx. openings" value={openings} />
        <WorkspaceField label="Timeline" value={lead.timeline} />
      </SectionCard>

      <SectionCard title="Source and status" icon={<InfoOutlinedIcon />}>
        <WorkspaceField label="Status" value={lead.status ? <CRMStatusChip status={lead.status} /> : null} />
        <WorkspaceField label="Source" value={humanizeEnum(lead.source)} />
        <WorkspaceField
          label="Linked employer"
          value={lead.employerId ? <RecordLink to={`/employers/${lead.employerId}`}>Open employer</RecordLink> : 'None yet'}
        />
        <WorkspaceField label="Received" mono value={formatDateTime(lead.createdAt)} />
        <WorkspaceField label="Lead ID" mono value={lead._id} />
      </SectionCard>
    </>
  );

  const timeline: TimelineEntry[] = lead.createdAt
    ? [{
      id: 'received',
      kind: 'submitted',
      text: `Lead received${lead.source ? ` from ${humanizeEnum(lead.source).toLowerCase()}` : ''}`,
      meta: formatDateTime(lead.createdAt),
    }]
    : [];

  return (
    <EntityWorkspace
      backLabel="Leads"
      onBack={() => navigate(lastListView('/leads'))}
      identityRail={identityRail}
      tabs={TABS}
      activeTabId="overview"
      // One section of content, so one tab and nothing to switch. EntityWorkspace requires a tab list.
      onTabChange={() => {}}
      activityRail={<ActivityRail timeline={timeline} />}
      activityOpen={activityOpen}
      onToggleActivity={toggleActivity}
    >
      {overview}
    </EntityWorkspace>
  );
};

export default LeadWorkspace;
